<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import {
  ClipboardList,
  ListChecks,
  Camera,
  FolderOpen,
  BookOpen,
} from 'lucide-vue-next'
import type { ProcessStep } from '@/types/landing'
import { useInView } from '@/composables/useInView'
import { useReducedMotion } from '@/composables/useReducedMotion'

interface Props {
  steps: ProcessStep[]
}

const props = defineProps<Props>()
const prefersReducedMotion = useReducedMotion()
const { isInView, targetRef } = useInView()
// 在模板中通过 ref="targetRef" 使用
void targetRef.value

const iconMap: Record<string, any> = {
  ClipboardList,
  ListChecks,
  Camera,
  FolderOpen,
  BookOpen,
}

const activeStep = ref(-1)
const isAnimated = ref(false)

// 触发依次高亮动画
watch(isInView, (inView) => {
  if (!inView || isAnimated.value) return
  if (prefersReducedMotion) {
    activeStep.value = props.steps.length - 1
    isAnimated.value = true
    return
  }
  props.steps.forEach((_, index) => {
    setTimeout(() => {
      activeStep.value = index
      if (index === props.steps.length - 1) {
        setTimeout(() => {
          isAnimated.value = true
        }, 600)
      }
    }, index * 300)
  })
})

const isMobile = ref(false)
if (typeof window !== 'undefined') {
  const mql = window.matchMedia('(max-width: 767px)')
  isMobile.value = mql.matches
  mql.addEventListener('change', (e) => { isMobile.value = e.matches })
}

// Lucide 图标尺寸
const iconSize = computed(() => isMobile.value ? 20 : 24)
</script>

<template>
  <section id="process" ref="targetRef" class="bg-white py-20 md:py-24">
    <div class="mx-auto max-w-[1200px] px-6">
      <!-- 标题 -->
      <div class="mb-14 text-center">
        <h2 class="mb-3 text-2xl font-semibold text-[#1D2129] md:text-3xl">
          从任务下发到台账生成，全程自动沉淀
        </h2>
        <p class="mx-auto max-w-[560px] text-base text-[#86909C]">
          工作做一遍，台账自动生成。让过程本身就是台账。
        </p>
      </div>

      <!-- 流程图 -->
      <div
        class="relative"
        :class="isMobile ? 'flex flex-col items-center gap-6' : 'flex items-start justify-between gap-4'"
      >
        <template v-for="(step, index) in steps" :key="step.step">
          <!-- 步骤节点 -->
          <div
            class="relative flex flex-col items-center text-center transition-all duration-300"
            :class="[
              isMobile ? 'w-full max-w-xs' : 'flex-1',
              activeStep >= index ? 'opacity-100' : 'opacity-30',
            ]"
          >
            <!-- 图标圆 -->
            <div
              class="mb-3 flex h-14 w-14 items-center justify-center rounded-full transition-all duration-300 md:h-16 md:w-16"
              :class="[
                activeStep >= index
                  ? 'scale-110 bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-lg'
                  : 'bg-[#F2F3F5] text-[#86909C]',
              ]"
            >
              <component :is="iconMap[step.icon as keyof typeof iconMap]" :size="iconSize" />
            </div>

            <!-- 步骤序号 -->
            <span
              class="mb-1 text-xs font-bold uppercase tracking-wider"
              :class="activeStep >= index ? 'text-indigo-600' : 'text-[#C9CDD4]'"
            >
              STEP {{ step.step }}
            </span>

            <!-- 标题 -->
            <h3 class="mb-1 text-base font-semibold text-[#1D2129] md:text-lg">
              {{ step.title }}
            </h3>

            <!-- 描述 -->
            <p class="mb-2 text-xs leading-relaxed text-[#86909C] md:text-sm">
              {{ step.description }}
            </p>

            <!-- 核心亮点标签 -->
            <span
              class="inline-block rounded-full px-3 py-0.5 text-xs font-medium"
              :class="activeStep >= index
                ? 'bg-indigo-50 text-indigo-700'
                : 'bg-[#F2F3F5] text-[#86909C]'"
            >
              {{ step.highlight }}
            </span>
          </div>

          <!-- 连接线 -->
          <div
            v-if="index < steps.length - 1"
            class="flex items-center self-center"
            :class="isMobile ? 'h-8 w-0' : 'h-0 flex-1 px-2'"
          >
            <div
              class="transition-all duration-500"
              :class="[
                isMobile ? 'h-8 w-0.5' : 'h-0.5 w-full',
                activeStep > index ? 'bg-indigo-500' : 'bg-[#E5E6EB]',
              ]"
            />
            <!-- 箭头 -->
            <svg
              v-if="!isMobile"
              class="flex-shrink-0 transition-colors duration-500"
              :class="activeStep > index ? 'text-indigo-500' : 'text-[#C9CDD4]'"
              width="16" height="16" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round"
            >
              <path d="M6 3l5 5-5 5" />
            </svg>
          </div>
        </template>
      </div>

      <!-- 闭环箭头（桌面端） -->
      <div v-if="isAnimated && !isMobile" class="mt-6 text-center">
        <div class="inline-flex items-center gap-2 text-sm text-indigo-600 animate-pulse">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
          <span>自动闭环，持续优化</span>
        </div>
      </div>

      <!-- 移动端底部提示 -->
      <div v-if="isAnimated && isMobile" class="mt-6 text-center">
        <div class="inline-flex items-center gap-2 text-sm text-indigo-600 animate-pulse">
          <span>完成闭环</span>
        </div>
      </div>
    </div>
  </section>
</template>
